import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  model,
  output,
} from '@angular/core';
import type { Company, Language, User } from '@indutrust/shared-model';
import { MetaTagsComponent } from '@indutrust/shared-ui';
import { provideTranslocoScopeSharedProfileHeader } from '@indutrust/shared-util';
import { TranslocoDirective } from '@jsverse/transloco';
import { Avatar } from 'primeng/avatar';
import { ButtonDirective } from 'primeng/button';
import { Divider } from 'primeng/divider';
import { Popover } from 'primeng/popover';
import { Tooltip } from 'primeng/tooltip';
import { I18nComponent } from '../i18n/i18n.component';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';

/**
 * User profile popover shown in the application header.
 *
 * Displays current {@link User} and {@link Company} information, allows changing the
 * active {@link Language} via {@link LanguageSelector}, provides a WhatsApp
 * contact shortcut, and exposes a logout action.
 *
 * @example
 * <idt-profile-header [user]="user" [company]="company" [currentLanguage]="lang" (logout)="onLogout()" />
 */
@Component({
  selector: 'idt-profile-header',
  imports: [
    Avatar,
    ButtonDirective,
    Divider,
    I18nComponent,
    MetaTagsComponent,
    Popover,
    ThemeToggleComponent,
    TitleCasePipe,
    Tooltip,
    TranslocoDirective,
  ],
  template: `
    <ng-container *transloco="let t; prefix: 'ph'">
      <p-avatar
        class="profile-avatar"
        image="assets/images/avatar/avatar-emirati.png"
        tabindex="0"
        tooltipPosition="bottom"
        [pTooltip]="isSmallScreen() ? null : t('tooltip')"
        (click)="profilePopover.toggle($event)"
        (keydown.enter)="profilePopover.toggle($event)" />

      <p-popover
        #profilePopover
        class="profile-popover"
        styleClass="no-popover-arrow">
        <div class="popover-content">
          <h5 class="title m-0">
            {{ user()?.email ?? '' }}
          </h5>
          <div class="flex gap-3 mt-2">
            <idt-meta-tags
              [roleLabel]="t('role', { role: (user()?.type | titlecase) })"
              [statusLabel]="
                t('status', { status: (company()?.status | titlecase) })
              " />
          </div>

          <p-divider />

          <div class="flex flex-col">
            <div class="flex items-center gap-1 mb-3">
              <i class="pi pi-cog"></i>
              {{ t('settings') }}
            </div>
            <div class="flex items-center gap-3">
              <idt-i18n />
              <idt-theme-toggle />
            </div>
          </div>

          <p-divider />

          <button
            class="w-full"
            pButton
            severity="secondary"
            type="button"
            (click)="logout.emit()">
            {{ t('logout') }} <i aria-hidden="true" class="pi pi-sign-out"></i>
          </button>
        </div>
      </p-popover>
    </ng-container>
  `,
  styles: `
    :host {
      display: flex;
    }

    .profile-avatar {
      cursor: pointer;
      border: 2px solid var(--p-text-color);
    }

    .popover-content {
      display: flex;
      flex-direction: column;
      min-width: 15.625rem;
      max-width: 20rem;

      .title {
        text-overflow: ellipsis;
        overflow: hidden;
        margin-bottom: 0.5rem;
      }
    }
  `,
  providers: [provideTranslocoScopeSharedProfileHeader()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileHeaderComponent {
  readonly company = input<Company | null>();
  readonly currentLanguage = input<Language | null>();
  readonly isSmallScreen = input<boolean>();
  readonly user = input<User | null>();

  readonly popoverVisible = model(false);

  readonly logout = output();
  readonly updateLanguage = output<Language>();
}
